// Inline-formatting parser for text blocks.
//
// The backend converts assistant HTML (e.g. <strong>X</strong>) into
// markdown-style markers in the text value (`**X**`). This module splits
// a text block's value into a sequence of `{ text, bold?, href? }` parts so
// MessageBlocks can render each part safely via `{{ }}` interpolation,
// wrapping bold parts in a styled span and linked parts in an anchor.
//
// SECURITY: each returned `text` field is a substring of the input
// (unchanged characters), still rendered through Vue's auto-escape.
// No HTML is constructed here; we only split on marker boundaries.
// `href` is only ever set to an http(s) URL, so a `javascript:` scheme can
// never reach the anchor.

import { isStructuredBlock } from './chat-structured';

const BOLD_PATTERN = /\*\*([\s\S]+?)\*\*/g;
const LINK_PATTERN = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>()]+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?'"]+$/;
// `https://riseup.co.il@evil.com` navigates to evil.com.
const USERINFO_HOST = /^https?:\/\/[^/?#]*@/i;

// One format for every message time the chat shows, in the bubbles and in the toast.
export function formatMessageTime(value) {
  if (!value) { return ''; }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) { return ''; }
  const pad = n => String(n).padStart(2, '0');
  const day = `${date.getDate()}.${date.getMonth() + 1}.${String(date.getFullYear()).slice(-2)}`;
  return `${day}, בשעה ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Splits every text block's inline parts into words, numbering the words across
// the whole message so a reveal can stagger them. Whitespace is kept as plain
// tokens (not words) so `pre-wrap` line breaks and wrapping are untouched.
// Table / chart blocks render whole (not from `value`), so they get no words
// and add nothing to the reveal's duration.
export function tokenizeForReveal(blocks, { wordStepMs, blockPauseMs }) {
  let wordIndex = 0;
  let textBlocks = 0;
  return (blocks || []).map(block => {
    if (block.type !== 'text' || isStructuredBlock(block)) { return null; }
    const pauseMs = textBlocks * blockPauseMs;
    textBlocks += 1;
    const parts = parseInline(block.value).map(part => ({
      ...part,
      tokens: part.text.split(/(\s+)/).filter(Boolean).map(text => {
        if (/^\s+$/.test(text)) { return { text }; }
        const token = { text, word: true, delayMs: pauseMs + wordIndex * wordStepMs };
        wordIndex += 1;
        return token;
      }),
    }));
    return { parts };
  });
}

// Delays only ever grow, so the last word is the last to finish.
export function revealDurationMs(blocks, config) {
  const words = tokenizeForReveal(blocks, config)
    .flatMap(block => (block ? block.parts.flatMap(part => part.tokens) : []))
    .filter(token => token.word);
  return words.length ? words[words.length - 1].delayMs + config.durationMs : 0;
}

export default function parseInline(value) {
  if (typeof value !== 'string' || value === '') {
    return [{ text: '' }];
  }
  const parts = [];
  let lastIndex = 0;
  let match = BOLD_PATTERN.exec(value);
  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push(...splitLinks(value.substring(lastIndex, match.index)));
    }
    parts.push(...splitLinks(match[1], true));
    lastIndex = BOLD_PATTERN.lastIndex;
    match = BOLD_PATTERN.exec(value);
  }
  if (lastIndex < value.length) {
    parts.push(...splitLinks(value.substring(lastIndex)));
  }
  return parts;
}

function splitLinks(value, bold) {
  const parts = [];
  let lastIndex = 0;
  LINK_PATTERN.lastIndex = 0;
  let match = LINK_PATTERN.exec(value);
  while (match !== null) {
    const [raw, label, target] = match;
    const href = target || raw.replace(TRAILING_PUNCTUATION, '');
    const end = match.index + (target ? raw.length : href.length);
    if (USERINFO_HOST.test(href)) {
      parts.push({ text: value.substring(lastIndex, end) });
    } else {
      if (match.index > lastIndex) {
        parts.push({ text: value.substring(lastIndex, match.index) });
      }
      parts.push({ text: label && label.trim() ? label : href, href });
    }
    lastIndex = end;
    LINK_PATTERN.lastIndex = lastIndex;
    match = LINK_PATTERN.exec(value);
  }
  if (lastIndex < value.length) {
    parts.push({ text: value.substring(lastIndex) });
  }
  return bold ? parts.map(part => ({ ...part, bold: true })) : parts;
}
